import { FC } from 'react';
import { useLocation } from 'react-use';

import { contentfulProps } from 'assets/mockData/emptyTypes';

import Flex from 'quarks/Flex';
import Heading from 'quarks/Heading';

import ComponentButton from 'components/Button/ComponentButton';
import ResourceCardDeck from 'components/CardDeck/ResourceCardDeck';
import ResourceSlider from 'components/CardDeck/ResourceSlider';
import TestimonialCardDeck from 'components/CardDeck/TestimonialCardDeck';

import colorizeText from 'utils/colorizeText';
import richTextParser from 'utils/richTextParser';

import type { ContentfulComponentSingleInstance } from 'graphqlTypes';

type ResourceHubProps = ContentfulComponentSingleInstance & {
  backgroundIsDark: boolean;
};

type CardsSectionProps = {
  type: string;
  link: string;
  sectionId: string;
  isTestimonial: boolean | undefined;
};

const scrollPosition = 'scroll-margin-top: 70px;';

const CardsSection: FC<CardsSectionProps> = ({ type, link, sectionId, isTestimonial }) => {
  const lowercaseType = sectionId?.toLowerCase();

  return (
    <Flex
      id={`${lowercaseType}-section`}
      flexDirection="column"
      gap="30px"
      css={scrollPosition}
      position="relative"
      paddingY={32}
    >
      <Flex width="100%" justifyContent="space-between" alignItems="center" gap="16px">
        <Heading as="h2" textStyle="h3" textColor="gray-900">
          {type}
        </Heading>
        <ComponentButton
          label="View all"
          link={link}
          buttonColor="Gray 900 Link"
          endIcon="Arrow Right"
          css={{
            '&.MuiButton-root': {
              maxWidth: '100% !important',
            },
          }}
        />
      </Flex>
      {isTestimonial ? (
        <TestimonialCardDeck {...contentfulProps} backgroundIsDark={false} isNoMoreButton />
      ) : (
        <ResourceCardDeck pageType={type} sectionId={sectionId} {...contentfulProps} backgroundIsDark={false} />
      )}
    </Flex>
  );
};

const ResourceHub: FC<ResourceHubProps> = ({ heading, body, backgroundIsDark }) => {
  const location = useLocation();

  const sections = [
    { type: 'Blog', id: 'blog', basePath: '' },
    { type: 'Case Studies', id: 'case-studies', basePath: '/resources' },
    { type: 'Eguides', id: 'eguides', basePath: '/resources' },
    { type: 'Infographics', id: 'infographics', basePath: '/resources' },
    { type: 'Podcast', id: 'podcast', basePath: '/resources' },
    { type: 'Reports', id: 'reports', basePath: '/resources' },
    { type: 'Testimonials', id: 'testimonials', isTestimonial: true, basePath: '/resources' },
    { type: 'Templates', id: 'templates', basePath: '/resources' },
    { type: 'Tips and Tools', id: 'tips-and-tools', basePath: '/resources' },
    { type: 'Videos', id: 'videos', basePath: '/resources' },
    { type: 'Webinars', id: 'webinar', basePath: '' },
  ];

  return (
    <Flex flexDirection="column">
      <Flex flexDirection="column" height="270px" overflowX="hidden" overflowY="hidden" sm={{ height: '250px' }}>
        <Flex
          position="relative"
          flexDirection="column"
          gap="24px"
          width="100%"
          maxWidth="770px"
          marginX="auto"
          textAlign="center"
        >
          {heading && (
            <Heading
              as="h1"
              fontSize="h4"
              fontWeight="bold"
              textColor="common-white"
              fontFamily="secondaryFont"
              md={{ fontSize: 'h3' }}
              lg={{ fontSize: 'h1' }}
            >
              {colorizeText(heading, 'navy-900')}
            </Heading>
          )}
          {body && (
            <Flex flexDirection="column" gap="16px" textColor="common-white">
              {body && richTextParser(body)}
            </Flex>
          )}
        </Flex>
        <Flex
          position="absolute"
          width="100vw"
          height="500px"
          top="0"
          left="0"
          zIndex="-1"
          backgroundColor="navy-900"
          md={{ height: '400px' }}
          lg={{ height: '500px' }}
        />
      </Flex>
      <ResourceSlider backgroundIsDark={backgroundIsDark} {...contentfulProps} />
      <Flex width="100%" gap="16px" alignItems="center" justifyContent="flex-start" flexWrap="wrap">
        {sections.map(({ type, id }, index) => (
          <ComponentButton key={index} label={type} link={`${location.pathname}#${id}-section`} className="!w-fit" />
        ))}
      </Flex>
      {sections.map(({ type, id, basePath, isTestimonial }, index) => (
        <CardsSection type={type} link={`${basePath}/${id}`} sectionId={id} isTestimonial={isTestimonial} key={index} />
      ))}
    </Flex>
  );
};

export default ResourceHub;
